import React, { useState, useEffect } from "react";
import {
  Card,
  Input,
  InputNumber,
  Button,
  message,
  Tabs,
  Typography,
  Row,
  Col,
  Table,
  Image,
  Select,
} from "antd";
import { SaveOutlined } from "@ant-design/icons";
import axios from "axios";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const FiniteSystemSizeModel = ({
  fileId,
  fileName,
  setSelectedFile,
  initialConfig,
}) => {
  const [activeTab, setActiveTab] = useState("1");
  const [loading, setLoading] = useState(false);
  const [resultData, setResultData] = useState(null);

  const [inputData, setInputData] = useState({
    name: fileName || "Untitled Analysis",
    arrival_rate: 5,
    service_rate: 10,
    servers: 3,
    max_system_size: 3,
    use_costs: true,
    time_unit: "Hours",
    server_cost: 12,
    waiting_cost: 12,
  });

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("username");
    window.location.href = "/login";
  };

  useEffect(() => {
    if (fileId) {
      fetchAnalysis();
    } else if (initialConfig) {
      setInputData((prev) => ({
        ...prev,
        ...initialConfig,
        name: initialConfig.name || prev.name || fileName || "Untitled Analysis",
      }));
    }
  }, [fileId, initialConfig]);

  const fetchAnalysis = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const url = `/api/retrieve_finiteSystemSizeModel/${fileId}/`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const { input_data, output_data, chart_url, name } = response.data;

      const restoredInput = {
        name: name || fileName || "Untitled Analysis",
        arrival_rate: input_data.arrival_rate,
        service_rate: input_data.service_rate,
        servers: input_data.servers,
        max_system_size: input_data.max_system_size,
        use_costs: input_data.use_costs,
        time_unit: input_data.time_unit,
        server_cost: input_data.server_cost,
        waiting_cost: input_data.waiting_cost,
      };

      setInputData(restoredInput);
      setResultData({ output_data, chart_url });
    } catch (error) {
      console.error("Fetch error:", error);
      if (error.response?.status === 401) {
        handleLogout();
      } else {
        message.error("Failed to fetch analysis details");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSolve = async () => {
    if (
      inputData.arrival_rate === null ||
      inputData.service_rate === null ||
      inputData.servers === null ||
      inputData.max_system_size === null
    ) {
      message.error("Please fill in all required inputs correctly.");
      return;
    }

    if (inputData.arrival_rate < 0) {
      message.error("Arrival rate cannot be negative.");
      return;
    }
    if (inputData.service_rate <= 0) {
      message.error("Service rate must be greater than zero.");
      return;
    }
    if (inputData.servers <= 0) {
      message.error("Number of servers must be greater than zero.");
      return;
    }
    if (inputData.max_system_size < 0) {
      message.error("Maximum system size cannot be negative.");
      return;
    }
    if (inputData.servers > inputData.max_system_size) {
      message.error("Error: Number of servers cannot be greater than the maximum system size.");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const payload = {
        name: inputData.name,
        input_data: {
          arrival_rate: inputData.arrival_rate,
          service_rate: inputData.service_rate,
          servers: inputData.servers,
          max_system_size: inputData.max_system_size,
          use_costs: inputData.use_costs,
          time_unit: inputData.time_unit,
          server_cost: inputData.use_costs ? inputData.server_cost : 0.0,
          waiting_cost: inputData.use_costs ? inputData.waiting_cost : 0.0,
        },
      };

      let response;
      if (fileId) {
        response = await axios.put(
          `/api/update_finiteSystemSizeModel/${fileId}/`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        response = await axios.post(
          "/api/save_finiteSystemSizeModel/",
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // Crucial Save Logic: Ensure the frontend immediately updates selectedFile.id after successful POST
        setSelectedFile({
          id: response.data.id,
          name: response.data.name,
          type: "finite_system_size",
        });
      }

      setResultData({
        output_data: response.data.output_data,
        chart_url: response.data.chart_url,
      });

      message.success("Analysis saved and solved successfully");
      setActiveTab("2"); // Switch to results tab
    } catch (error) {
      console.error("Solve error:", error);
      if (error.response?.status === 401) {
        handleLogout();
      } else {
        message.error(error.response?.data?.error || "Failed to solve analysis");
      }
    } finally {
      setLoading(false);
    }
  };

  const renderOutputTable = () => {
    if (!resultData || !resultData.output_data) return null;

    const { solution_table, probability_table } = resultData.output_data;

    const metricsData = [];
    if (solution_table) {
      const getFormattedTime = (obj) => {
        if (!obj) return "-";
        const val = obj.value !== undefined && obj.value !== null ? obj.value.toFixed(4) : "-";
        const minStr = obj.minutes !== undefined && obj.minutes !== null ? `${obj.minutes.toFixed(2)} mins` : "";
        const secStr = obj.seconds !== undefined && obj.seconds !== null ? `${obj.seconds.toFixed(2)} secs` : "";
        const parts = [minStr, secStr].filter(Boolean);
        return parts.length > 0 ? `${val} (${parts.join(", ")})` : val;
      };

      metricsData.push(
        {
          key: "utilization",
          metric: "Average Server Utilization",
          value: solution_table.average_server_utilization !== undefined ? solution_table.average_server_utilization.toFixed(4) : "-",
        },
        {
          key: "lq",
          metric: "Average Number in Line (Lq)",
          value: solution_table.average_number_in_line_Lq !== undefined ? solution_table.average_number_in_line_Lq.toFixed(4) : "-",
        },
        {
          key: "l",
          metric: "Average Number in System (L)",
          value: solution_table.average_number_in_system_L !== undefined ? solution_table.average_number_in_system_L.toFixed(4) : "-",
        },
        {
          key: "wq",
          metric: "Average Time in Line (Wq)",
          value: getFormattedTime(solution_table.average_time_in_line_Wq),
        },
        {
          key: "w",
          metric: "Average Time in System (W)",
          value: getFormattedTime(solution_table.average_time_in_system_W),
        },
        {
          key: "effective_arrival",
          metric: "Effective Arrival Rate",
          value: solution_table.effective_arrival_rate !== undefined ? solution_table.effective_arrival_rate.toFixed(4) : "-",
        },
        {
          key: "full_prob",
          metric: "Probability System is Full (Pk)",
          value: solution_table.probability_system_is_full !== undefined ? solution_table.probability_system_is_full.toFixed(4) : "-",
        }
      );

      if (solution_table.total_cost !== undefined && solution_table.total_cost !== null) {
        metricsData.push({
          key: "total_cost",
          metric: "Total Cost",
          value: `$${solution_table.total_cost.toFixed(4)}`,
        });
      }
    }

    const metricsColumns = [
      { title: "Metric", dataIndex: "metric", key: "metric", width: "50%" },
      { title: "Value", dataIndex: "value", key: "value", width: "50%" },
    ];

    const probColumns = [
      { title: "State (k)", dataIndex: "k", key: "k" },
      {
        title: "Probability (P = k)",
        dataIndex: "prob_eq_k",
        key: "prob_eq_k",
        render: (val) => (val !== undefined && val !== null ? val.toFixed(4) : "-"),
      },
      {
        title: "Cumulative Probability (P <= k)",
        dataIndex: "prob_le_k",
        key: "prob_le_k",
        render: (val) => (val !== undefined && val !== null ? val.toFixed(4) : "-"),
      },
      {
        title: "Probability (P > k)",
        dataIndex: "prob_gt_k",
        key: "prob_gt_k",
        render: (val) => (val !== undefined && val !== null ? val.toFixed(4) : "-"),
      },
    ];

    const probData = (probability_table || []).map((row, index) => ({
      key: `prob_${index}`,
      ...row,
    }));

    return (
      <Row gutter={[24, 24]}>
        <Col span={24} lg={12}>
          <Card title="Queueing Metrics" size="small" style={{ borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
            <Table
              dataSource={metricsData}
              columns={metricsColumns}
              pagination={false}
              bordered
              size="middle"
            />
          </Card>
        </Col>
        <Col span={24} lg={12}>
          <Card title="State Probability Table" size="small" style={{ borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
            <Table
              dataSource={probData}
              columns={probColumns}
              pagination={false}
              bordered
              size="middle"
              scroll={{ y: 350 }}
            />
          </Card>
        </Col>
      </Row>
    );
  };

  const renderChart = () => {
    if (!resultData || !resultData.chart_url) {
      return (
        <Card title="Probability Distribution Chart" style={{ marginTop: 24, borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
          <div style={{ textAlign: "center", padding: 24, color: "#999" }}>
            No chart generated yet. Solve the model to generate and view the chart.
          </div>
        </Card>
      );
    }

    return (
      <Card title="Probability Distribution Chart" style={{ marginTop: 24, borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
        <div style={{ display: "flex", justifyContent: "center", padding: 12 }}>
          <Image
            src={resultData.chart_url}
            alt="Probability Distribution Chart"
            style={{ maxWidth: "100%", maxHeight: 500, borderRadius: 4 }}
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
          />
        </div>
      </Card>
    );
  };

  return (
    <Card
      title={
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Title level={4} style={{ margin: 0 }}>
            {inputData.name}
          </Title>
          <Text type="secondary">Finite System Size Model (M/M/s/K)</Text>
        </div>
      }
      extra={
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleSolve}
          loading={loading}
          size="large"
          style={{ borderRadius: 6, fontWeight: 500 }}
        >
          {fileId ? "Update & Solve" : "Save & Solve"}
        </Button>
      }
      style={{ borderRadius: 12, boxShadow: "0 4px 16px rgba(0,0,0,0.05)", border: "1px solid #f0f0f0" }}
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab} style={{ marginTop: -12 }}>
        <TabPane tab="Inputs" key="1">
          <Row gutter={[24, 24]} style={{ padding: "12px 0" }}>
            <Col span={24} md={12}>
              <Card
                title="System Parameters"
                bordered={false}
                style={{
                  backgroundColor: "#fafafa",
                  borderRadius: 8,
                  height: "100%",
                }}
              >
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Analysis Name</Text>
                  <Input
                    value={inputData.name}
                    onChange={(e) => setInputData((prev) => ({ ...prev, name: e.target.value }))}
                    style={{ width: "100%", marginTop: 8 }}
                    placeholder="Enter analysis name"
                  />
                </div>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Text strong>Arrival Rate (λ)</Text>
                    <InputNumber
                      min={0}
                      step={0.1}
                      value={inputData.arrival_rate}
                      onChange={(val) => setInputData((prev) => ({ ...prev, arrival_rate: val }))}
                      style={{ width: "100%", marginTop: 8 }}
                    />
                  </Col>
                  <Col span={12}>
                    <Text strong>Service Rate (μ)</Text>
                    <InputNumber
                      min={0.001}
                      step={0.1}
                      value={inputData.service_rate}
                      onChange={(val) => setInputData((prev) => ({ ...prev, service_rate: val }))}
                      style={{ width: "100%", marginTop: 8 }}
                    />
                  </Col>
                  <Col span={12}>
                    <Text strong>Number of Servers (s)</Text>
                    <InputNumber
                      min={1}
                      precision={0}
                      value={inputData.servers}
                      onChange={(val) => setInputData((prev) => ({ ...prev, servers: val }))}
                      style={{ width: "100%", marginTop: 8 }}
                    />
                  </Col>
                  <Col span={12}>
                    <Text strong>Max System Size (K)</Text>
                    <InputNumber
                      min={1}
                      precision={0}
                      value={inputData.max_system_size}
                      onChange={(val) => setInputData((prev) => ({ ...prev, max_system_size: val }))}
                      style={{ width: "100%", marginTop: 8 }}
                    />
                  </Col>
                  <Col span={24}>
                    <Text strong>Time Unit</Text>
                    <Select
                      value={inputData.time_unit}
                      onChange={(val) => setInputData((prev) => ({ ...prev, time_unit: val }))}
                      style={{ width: "100%", marginTop: 8 }}
                    >
                      {["Seconds", "Minutes", "Hours", "Days", "Weeks", "Months", "Quarters", "Years"].map((u) => (
                        <Select.Option key={u} value={u}>
                          {u}
                        </Select.Option>
                      ))}
                    </Select>
                  </Col>
                </Row>
              </Card>
            </Col>
            <Col span={24} md={12}>
              <Card
                title="Cost Configuration"
                bordered={false}
                style={{
                  backgroundColor: "#fafafa",
                  borderRadius: 8,
                  height: "100%",
                }}
              >
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Evaluate Costs?</Text>
                  <Select
                    value={inputData.use_costs}
                    onChange={(val) => setInputData((prev) => ({ ...prev, use_costs: val }))}
                    style={{ width: "100%", marginTop: 8 }}
                  >
                    <Select.Option value={true}>Yes</Select.Option>
                    <Select.Option value={false}>No</Select.Option>
                  </Select>
                </div>
                {inputData.use_costs && (
                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <Text strong>Server Cost (per unit time)</Text>
                      <InputNumber
                        min={0}
                        value={inputData.server_cost}
                        onChange={(val) => setInputData((prev) => ({ ...prev, server_cost: val }))}
                        style={{ width: "100%", marginTop: 8 }}
                      />
                    </Col>
                    <Col span={12}>
                      <Text strong>Waiting Cost (per customer)</Text>
                      <InputNumber
                        min={0}
                        value={inputData.waiting_cost}
                        onChange={(val) => setInputData((prev) => ({ ...prev, waiting_cost: val }))}
                        style={{ width: "100%", marginTop: 8 }}
                      />
                    </Col>
                  </Row>
                )}
              </Card>
            </Col>
          </Row>
        </TabPane>
        <TabPane tab="Results" key="2" disabled={!resultData}>
          <div style={{ padding: "12px 0" }}>
            {renderOutputTable()}
            {renderChart()}
          </div>
        </TabPane>
      </Tabs>
    </Card>
  );
};

export default FiniteSystemSizeModel;
